import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useValues, PRESET_VALUES, PresetValue } from '../lib/stores/values';
import { useLifeAreas } from '../lib/stores/lifeareas';
import Card from '../lib/design-system/components/Card';
import Button from '../lib/design-system/components/Button';
import { Modal } from '../lib/design-system/components';

const MAX_VALUES = 12;

export default function ValuesPage() {
  const navigate = useNavigate();
  const { values, loading, error, initialize, addValue, rankValues, removeValue, updateValueAreas, initialized } = useValues();
  const { areas, initialize: initAreas, initialized: areasInitialized } = useLifeAreas();
  const [selectedCategory, setSelectedCategory] = useState<string>('All');
  const [showAddCustom, setShowAddCustom] = useState(false);
  const [customName, setCustomName] = useState('');
  const [customDescription, setCustomDescription] = useState('');
  const [isComplete, setIsComplete] = useState(false);
  const [showMaxModal, setShowMaxModal] = useState(false);
  const [showMinModal, setShowMinModal] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  const [expandedValueId, setExpandedValueId] = useState<string | null>(null);

  useEffect(() => {
    if (!initialized) initialize();
    if (!areasInitialized) initAreas();
  }, [initialized, areasInitialized]);

  const categories = ['All', ...Array.from(new Set(PRESET_VALUES.map(v => v.category)))];

  const filteredPresets = selectedCategory === 'All'
    ? PRESET_VALUES
    : PRESET_VALUES.filter(v => v.category === selectedCategory);

  const isValueSelected = (name: string) => {
    return values.some(v => v.value_name === name);
  };

  const handleAddPreset = async (preset: PresetValue) => {
    if (values.length >= MAX_VALUES) {
      setShowMaxModal(true);
      return;
    }
    if (!isValueSelected(preset.name)) {
      await addValue(preset.name, preset.description);
    }
  };

  const handleAddCustom = async () => {
    if (values.length >= MAX_VALUES) {
      setShowMaxModal(true);
      return;
    }
    if (customName.trim()) {
      await addValue(customName.trim(), customDescription.trim() || undefined);
      setCustomName('');
      setCustomDescription('');
      setShowAddCustom(false);
    }
  };

  const handleMarkComplete = () => {
    if (values.length < 3) {
      setShowMinModal(true);
      return;
    }
    setIsComplete(true);
  };

  const handleMoveUp = async (index: number) => {
    if (index > 0) {
      const newOrder = [...values];
      [newOrder[index - 1], newOrder[index]] = [newOrder[index], newOrder[index - 1]];
      try {
        await rankValues(newOrder.map(v => v.id));
      } catch (err) {
        console.error('Failed to reorder values:', err);
      }
    }
  };

  const handleMoveDown = async (index: number) => {
    if (index < values.length - 1) {
      const newOrder = [...values];
      [newOrder[index], newOrder[index + 1]] = [newOrder[index + 1], newOrder[index]];
      try {
        await rankValues(newOrder.map(v => v.id));
      } catch (err) {
        console.error('Failed to reorder values:', err);
      }
    }
  };

  const handleToggleArea = async (valueId: string, areaId: string) => {
    const value = values.find(v => v.id === valueId);
    if (!value) return;

    const currentAreas = value.life_area_ids || [];
    const newAreas = currentAreas.includes(areaId)
      ? currentAreas.filter(id => id !== areaId)
      : [...currentAreas, areaId];

    await updateValueAreas(valueId, newAreas);
  };

  if (loading && !initialized) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <p>Loading values...</p>
      </div>
    );
  }

  return (
    <div className="page-container">
      {/* Header */}
      <div style={{ marginBottom: 'clamp(1.5rem, 3vw, 2rem)' }}>
        <h1 style={{
          fontFamily: 'var(--font-serif)',
          fontSize: 'clamp(1.75rem, 4vw, 2.5rem)',
          fontWeight: 400,
          color: 'var(--color-neutral-900)',
          marginBottom: '0.5rem',
        }}>
          Core Values
        </h1>
        <p style={{ color: 'var(--color-neutral-600)', fontSize: 'clamp(1rem, 2vw, 1.125rem)' }}>
          Identify and rank the principles that guide your life
        </p>
      </div>

      {error && (
        <div style={{
          padding: '1rem',
          backgroundColor: 'rgba(239, 68, 68, 0.1)',
          border: '1px solid rgba(239, 68, 68, 0.3)',
          borderRadius: '8px',
          color: '#DC2626',
          marginBottom: '1.5rem',
        }}>
          {error}
        </div>
      )}

      <style>{`
        .values-layout {
          display: grid;
          grid-template-columns: minmax(min(100%, 240px), 280px) 1fr;
          gap: clamp(1rem, 2vw, 2rem);
        }
        @media (max-width: 768px) {
          .values-layout {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
      <div className="values-layout">
        {/* Left: Selected Values */}
        <div>
          <Card style={{ padding: '1.5rem' }}>
            <div style={{ marginBottom: '1rem' }}>
              <h3 style={{ fontSize: '1.25rem', fontWeight: 600, marginBottom: '0.5rem' }}>
                Your Values ({values.length}/{MAX_VALUES})
              </h3>
              <p style={{ fontSize: '0.875rem', color: 'var(--color-neutral-600)' }}>
                Ranked by importance
              </p>
              {values.length >= MAX_VALUES && (
                <div style={{
                  marginTop: '0.5rem',
                  padding: '0.5rem',
                  backgroundColor: 'rgba(251, 146, 60, 0.1)',
                  border: '1px solid rgba(251, 146, 60, 0.3)',
                  borderRadius: '6px',
                  fontSize: '0.75rem',
                  color: '#EA580C',
                }}>
                  ⚠️ Maximum values reached. Remove some to add more.
                </div>
              )}
            </div>

            {values.length === 0 ? (
              <div style={{
                padding: '2rem 1rem',
                textAlign: 'center',
                color: 'var(--color-neutral-500)',
                fontSize: '0.875rem',
              }}>
                Select values from the list →
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {values.filter(v => v.id != null).map((value, index) => {
                  const isExpanded = expandedValueId === value.id;
                  return (
                    <div key={value.id}>
                      <div
                        onClick={() => setExpandedValueId(isExpanded ? null : value.id)}
                        style={{
                          padding: '0.75rem',
                          backgroundColor: 'var(--color-primary-50)',
                          border: '2px solid var(--color-primary-200)',
                          borderRadius: '8px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          cursor: 'pointer',
                        }}
                      >
                        <div style={{
                          minWidth: '1.5rem',
                          width: '1.5rem',
                          height: '1.5rem',
                          flexShrink: 0,
                          backgroundColor: 'var(--color-primary-600)',
                          color: 'white',
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '0.75rem',
                          fontWeight: 700,
                        }}>
                          {index + 1}
                        </div>
                        <div style={{
                          flex: 1,
                          fontWeight: 600,
                          fontSize: '0.875rem',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          minWidth: 0,
                        }}>
                          {value.value_name}
                        </div>
                        {value.life_area_ids && value.life_area_ids.length > 0 && (
                          <div style={{
                            fontSize: '0.75rem',
                            color: 'var(--color-neutral-600)',
                            backgroundColor: 'var(--color-neutral-100)',
                            padding: '0.125rem 0.5rem',
                            borderRadius: '10px',
                          }}>
                            {value.life_area_ids.length} area{value.life_area_ids.length > 1 ? 's' : ''}
                          </div>
                        )}
                        <div style={{ display: 'flex', gap: '0.25rem', flexShrink: 0 }}>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleMoveUp(index);
                            }}
                            disabled={index === 0}
                            style={{
                              padding: '0.25rem',
                              border: 'none',
                              background: 'transparent',
                              cursor: index === 0 ? 'not-allowed' : 'pointer',
                              opacity: index === 0 ? 0.3 : 1,
                              fontSize: '1rem',
                            }}
                            title="Move up"
                          >
                            ↑
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleMoveDown(index);
                            }}
                            disabled={index === values.length - 1}
                            style={{
                              padding: '0.25rem',
                              border: 'none',
                              background: 'transparent',
                              cursor: index === values.length - 1 ? 'not-allowed' : 'pointer',
                              opacity: index === values.length - 1 ? 0.3 : 1,
                              fontSize: '1rem',
                            }}
                            title="Move down"
                          >
                            ↓
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              removeValue(value.id);
                            }}
                            style={{
                              padding: '0.25rem 0.5rem',
                              border: 'none',
                              background: 'transparent',
                              cursor: 'pointer',
                              color: 'var(--color-error-600)',
                              fontSize: '1rem',
                            }}
                            title="Remove"
                          >
                            ×
                          </button>
                        </div>
                      </div>

                      {/* Expanded Area Selection */}
                      {isExpanded && (
                        <div style={{
                          marginTop: '0.5rem',
                          padding: '1rem',
                          backgroundColor: 'white',
                          border: '1px solid var(--color-primary-200)',
                          borderRadius: '8px',
                        }}>
                          <div style={{
                            fontSize: '0.875rem',
                            fontWeight: 600,
                            marginBottom: '0.75rem',
                            color: 'var(--color-neutral-700)',
                          }}>
                            Which life areas does "{value.value_name}" apply to?
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                            {areas.map(area => {
                              const isSelected = (value.life_area_ids || []).includes(area.id);
                              return (
                                <label
                                  key={area.id}
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.75rem',
                                    padding: '0.5rem',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    backgroundColor: isSelected ? `${area.color}15` : 'transparent',
                                    border: isSelected ? `1px solid ${area.color}` : '1px solid var(--color-neutral-200)',
                                  }}
                                >
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={() => handleToggleArea(value.id, area.id)}
                                    style={{ cursor: 'pointer' }}
                                  />
                                  <span style={{ fontSize: '1.25rem' }}>{area.icon}</span>
                                  <span style={{ fontSize: '0.875rem', fontWeight: 500 }}>
                                    {area.name}
                                  </span>
                                </label>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            <div style={{ marginTop: '1rem' }}>
              <Button onClick={() => setShowAddCustom(!showAddCustom)} style={{ width: '100%' }} disabled={values.length >= MAX_VALUES}>
                {showAddCustom ? 'Cancel' : '+ Add Custom Value'}
              </Button>

              {showAddCustom && (
                <div style={{ marginTop: '1rem', padding: '1rem', backgroundColor: 'var(--color-neutral-50)', borderRadius: '8px' }}>
                  <input
                    type="text"
                    value={customName}
                    onChange={(e) => setCustomName(e.target.value)}
                    placeholder="Value name..."
                    style={{
                      width: '100%',
                      padding: '0.5rem',
                      border: '1px solid var(--color-neutral-300)',
                      borderRadius: '6px',
                      marginBottom: '0.5rem',
                    }}
                  />
                  <textarea
                    value={customDescription}
                    onChange={(e) => setCustomDescription(e.target.value)}
                    placeholder="Description (optional)..."
                    rows={2}
                    style={{
                      width: '100%',
                      padding: '0.5rem',
                      border: '1px solid var(--color-neutral-300)',
                      borderRadius: '6px',
                      marginBottom: '0.5rem',
                      fontFamily: 'inherit',
                    }}
                  />
                  <Button onClick={handleAddCustom} disabled={!customName.trim()} style={{ width: '100%' }}>
                    Add Custom Value
                  </Button>
                </div>
              )}
            </div>

            {/* Mark Complete & Next Steps */}
            {values.length >= 3 && !isComplete && (
              <div style={{ marginTop: '1.5rem', paddingTop: '1.5rem', borderTop: '1px solid var(--color-neutral-200)' }}>
                <Button onClick={handleMarkComplete} style={{ width: '100%', backgroundColor: 'var(--color-success-600)' }}>
                  ✓ Mark Complete
                </Button>
              </div>
            )}

            {isComplete && (
              <div style={{ marginTop: '1.5rem', paddingTop: '1.5rem', borderTop: '1px solid var(--color-neutral-200)' }}>
                <div style={{
                  padding: '1rem',
                  backgroundColor: 'var(--color-success-50)',
                  border: '1px solid var(--color-success-200)',
                  borderRadius: '8px',
                  marginBottom: '1rem',
                  textAlign: 'center',
                }}>
                  <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>🎉</div>
                  <div style={{ fontWeight: 600, color: 'var(--color-success-700)', marginBottom: '0.25rem' }}>
                    Values Complete!
                  </div>
                  <div style={{ fontSize: '0.875rem', color: 'var(--color-neutral-600)' }}>
                    You've identified your core values
                  </div>
                </div>
                <Button onClick={() => navigate('/purpose/wheel')} style={{ width: '100%' }}>
                  Next: Life Wheel →
                </Button>
              </div>
            )}
          </Card>
        </div>

        {/* Right: Preset Values */}
        <div>
          {/* Category Filters */}
          <div style={{ marginBottom: '1.5rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            {categories.map(cat => (
              <button
                key={cat}
                onClick={() => setSelectedCategory(cat)}
                style={{
                  padding: '0.5rem 1rem',
                  border: selectedCategory === cat ? '2px solid var(--color-primary-600)' : '1px solid var(--color-neutral-300)',
                  borderRadius: '8px',
                  background: selectedCategory === cat ? 'var(--color-primary-50)' : 'white',
                  color: selectedCategory === cat ? 'var(--color-primary-700)' : 'var(--color-neutral-700)',
                  fontWeight: selectedCategory === cat ? 600 : 400,
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                }}
              >
                {cat}
              </button>
            ))}
          </div>

          {/* Preset Values Grid */}
          <div className="grid-dense">
            {filteredPresets.map(preset => {
              const selected = isValueSelected(preset.name);
              return (
                <div
                  key={preset.name}
                  style={{ cursor: selected ? 'default' : 'pointer' }}
                  onClick={(e) => {
                    e.preventDefault();
                    if (!selected) {
                      handleAddPreset(preset);
                    }
                  }}
                >
                  <Card
                    style={{
                      padding: '1rem',
                      border: selected ? '2px solid var(--color-success-500)' : '1px solid var(--color-neutral-200)',
                      backgroundColor: selected ? 'var(--color-success-50)' : 'white',
                      opacity: selected ? 0.7 : 1,
                      height: '100%',
                    }}
                  >
                  <div style={{ display: 'flex', alignItems: 'start', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                    <h4 style={{
                      fontSize: '1rem',
                      fontWeight: 600,
                      color: 'var(--color-neutral-900)',
                      margin: 0,
                    }}>
                      {preset.name}
                    </h4>
                    {selected && (
                      <span style={{ color: 'var(--color-success-600)', fontSize: '1.25rem' }}>✓</span>
                    )}
                  </div>
                  <p style={{
                    fontSize: '0.875rem',
                    color: 'var(--color-neutral-600)',
                    lineHeight: '1.5',
                    margin: 0,
                  }}>
                    {preset.description}
                  </p>
                  <div style={{
                    marginTop: '0.5rem',
                    fontSize: '0.75rem',
                    color: 'var(--color-neutral-500)',
                    fontWeight: 500,
                  }}>
                    {preset.category}
                  </div>
                </Card>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Modals */}
      <Modal
        isOpen={showMaxModal}
        onClose={() => setShowMaxModal(false)}
        title="Maximum Values Reached"
      >
        You can select a maximum of {MAX_VALUES} values. Remove some values to add new ones.
      </Modal>

      <Modal
        isOpen={showMinModal}
        onClose={() => setShowMinModal(false)}
        title="Minimum Values Required"
      >
        Please select at least 3 values before marking as complete.
      </Modal>
    </div>
  );
}
